import { useState } from 'react';
import { FileText, BarChart3, Settings, Clock, Zap, X } from 'lucide-react';
import Invoices from './facturacio/Invoices';
import { Button } from '../components/ui/button';

type TabValue = 'invoices' | 'face' | 'reports' | 'pending' | 'settings';

const tabs = [
  { id: 'invoices' as TabValue, label: 'Factures', icon: FileText },
  { id: 'face' as TabValue, label: 'FACe', icon: Zap },
  { id: 'reports' as TabValue, label: 'Informes', icon: BarChart3 },
  { id: 'pending' as TabValue, label: 'Pendents', icon: Clock },
  { id: 'settings' as TabValue, label: 'Configuració', icon: Settings },
];

export default function Facturacio() {
  const [activeTab, setActiveTab] = useState<TabValue>('invoices');
  const [faceModalOpen, setFaceModalOpen] = useState(false);
  const [faceApiUrl, setFaceApiUrl] = useState('');
  const [faceToken, setFaceToken] = useState('');
  const [faceConnected, setFaceConnected] = useState(false);

  const handleConnectFace = async () => {
    if (!faceApiUrl.trim()) {
      alert('Por favor, ingresa la URL de la API de FACe');
      return;
    }
    
    console.log('Conectando con FACe:', { faceApiUrl, faceToken });
    setFaceConnected(true);
    setFaceModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Facturació Electrònica</h1>
        <p className="text-muted-foreground">Gestió de factures i conformitat fiscal</p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-border">
        <nav className="flex gap-4 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'invoices' && <Invoices />}
        
        {activeTab === 'face' && (
          <div className="space-y-6">
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-4">
                <Zap className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Factura Electrónica (FACe)</h3>
                  <p className="text-blue-800 mb-4">Plataforma de facturación electrónica del ministerio español per a contractes de la Administración Pública.</p>
                  <button 
                    onClick={() => setFaceModalOpen(true)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
                  >
                    {faceConnected ? 'Configuración de FACe' : 'Connectar amb FACe'}
                  </button>
                </div>
              </div>
            </div>

          </div>
        )}
        
        {activeTab === 'reports' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Informes de Facturació</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer">
                <h4 className="font-semibold mb-1">Resum Mensual</h4>
                <p className="text-sm text-muted-foreground mb-3">Total facturat per mes i comparativa</p>
                <button className="text-sm text-primary hover:underline">Veure informe →</button>
              </div>
              <div className="p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer">
                <h4 className="font-semibold mb-1">Analítica de Clients</h4>
                <p className="text-sm text-muted-foreground mb-3">Clients més actius i ingressos</p>
                <button className="text-sm text-primary hover:underline">Veure informe →</button>
              </div>
              <div className="p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer">
                <h4 className="font-semibold mb-1">Estat de Pagaments</h4>
                <p className="text-sm text-muted-foreground mb-3">Pagaments rebuts i pendents</p>
                <button className="text-sm text-primary hover:underline">Veure informe →</button>
              </div>
              <div className="p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer">
                <h4 className="font-semibold mb-1">Proyeccions de Cobrament</h4>
                <p className="text-sm text-muted-foreground mb-3">Taxa de cobrament i projectes futurs</p>
                <button className="text-sm text-primary hover:underline">Veure informe →</button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'pending' && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">Factures Pendents de Pagament</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-600 font-medium">Factures Vencides</p>
                <p className="text-2xl font-bold text-red-700">0</p>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-sm text-yellow-600 font-medium">A Vencer en 30 Dies</p>
                <p className="text-2xl font-bold text-yellow-700">0</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="text-sm text-blue-600 font-medium">Total Pendent</p>
                <p className="text-2xl font-bold text-blue-700">0,00 €</p>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left">Número</th>
                    <th className="px-4 py-3 text-left">Client</th>
                    <th className="px-4 py-3 text-right">Import</th>
                    <th className="px-4 py-3 text-left">Venciment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      No hi ha factures pendents
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <h3 className="text-sm font-semibold">Configuració de Facturació</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 border rounded-xl">
                <label className="block text-sm font-semibold mb-2">Sèrie de Factures</label>
                <input 
                  type="text" 
                  placeholder="Ex: FAC" 
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="p-4 border rounded-xl">
                <label className="block text-sm font-semibold mb-2">Número Inicial</label>
                <input 
                  type="number" 
                  placeholder="Ex: 1" 
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="p-4 border rounded-xl">
                <label className="block text-sm font-semibold mb-2">Termini de Pagament (dies)</label>
                <input 
                  type="number" 
                  placeholder="Ex: 30" 
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="p-4 border rounded-xl">
                <label className="block text-sm font-semibold mb-2">IVA per Defecte (%)</label>
                <input 
                  type="number" 
                  placeholder="Ex: 21" 
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="p-4 border rounded-lg">
              <label className="block text-sm font-semibold mb-2">Condicions de Pagament</label>
              <textarea 
                placeholder="Especificar les condicions de pagament..."
                rows={4}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <Button className="bg-green-600 hover:bg-green-700">Desa Configuració</Button>
          </div>
        )}
      </div>

      {/* FACe Connection Modal */}
      {faceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-sm font-semibold">Conexió amb FACe</h2>
              <button
                onClick={() => setFaceModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">URL de l'API FACe</label>
                <input
                  type="url"
                  placeholder="https://face.sea.gob.es/..."
                  value={faceApiUrl}
                  onChange={(e) => setFaceApiUrl(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Token d'Accés (opcional)</label>
                <input
                  type="password"
                  placeholder="Token d'accés"
                  value={faceToken}
                  onChange={(e) => setFaceToken(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <p className="text-sm text-gray-600">
                Configuració les dades de connexió amb la plataforma FACe del ministerio per enviar factures electròniques.
              </p>
            </div>

            <div className="flex gap-3 p-6 border-t">
              <button
                onClick={() => setFaceModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel·lar
              </button>
              <button
                onClick={handleConnectFace}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Connectar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
